import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, Camera, FlipHorizontal } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { useGlobalState } from '../../utils/GlobalState';

const UploadPhoto = () => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const videoRef = useRef(null);
  const photoRef = useRef(null);
  const navigate = useNavigate();
  const { state, updateState } = useGlobalState();

  const handleDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    setFile(uploadedFile);
    setPreview(URL.createObjectURL(uploadedFile));
    updateState({ uploadedImage: URL.createObjectURL(uploadedFile) });
  };

  const handleUpload = () => {
    if (!file) {
      alert("Please upload an image first!");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageDataUrl = reader.result;
      updateState({ uploadedImage: imageDataUrl });
      console.log("Image saved to global state:", imageDataUrl);
      navigate("/muralabeling");
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = () => {
    setFile(null);
    setPreview(null);
    updateState({ uploadedImage: null });
  };

  useEffect(() => {
    if (!isCameraOpen) return;

    const startCamera = async () => {
      try {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }

        const constraints = {
          video: { 
            facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(mediaStream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        alert("Could not access the camera. Please check permissions.");
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [facingMode, isCameraOpen]);

  const startCamera = async () => {
    setIsCameraOpen(true);
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const takePhoto = () => {
    const video = videoRef.current;
    const photo = photoRef.current;
    
    photo.width = video.videoWidth;
    photo.height = video.videoHeight;
    
    const ctx = photo.getContext('2d');
    ctx.drawImage(video, 0, 0, photo.width, photo.height);
    
    photo.toBlob((blob) => {
      const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
      setFile(file);
      setPreview(URL.createObjectURL(blob));
      updateState({ uploadedImage: URL.createObjectURL(blob) });
      stopCamera();
    }, 'image/jpeg', 0.95);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    accept: "image/*",
    disabled: !!file,
  });

  return (
    <div className="flex flex-col justify-between min-h-screen bg-black text-white p-4 sm:p-6 md:p-8 lg:p-10">
      <button
        onClick={() => navigate("/mura-labeling")}
        className="absolute top-4 left-4 p-2 text-white"
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      <h2 className="text-center text-lg sm:text-xl md:text-2xl font-semibold mt-10">
        Upload Photo
      </h2>

      <div className="bg-black rounded-lg shadow-md w-full max-w-full mx-auto flex-grow flex flex-col justify-start mt-6">
        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-center mt-4">
          Refinement of the model through photos
        </h3>
        <p className="text-gray-400 text-xs sm:text-sm text-center mt-1">
          Check the tips to take a photo and ensure the photo is clear and readable.
        </p>

        <div className="flex justify-center w-full mt-4">
          <div
            {...getRootProps()}
            className="flex flex-col justify-center items-center w-full px-2 sm:w-2/3 h-[12rem] sm:h-[20rem] md:h-[25rem] lg:w-1/2 lg:h-[30rem] border-2  border-dashed border-gray-600 mt-4 cursor-pointer hover:border-gray-400 transition relative overflow-hidden"
          >
            <input {...getInputProps()} />
            {!file ? (
              <>
                <p className="text-gray-300 p-2 text-xs sm:text-sm text-center">
                  Drag and drop file here or click to upload
                </p>
                <div className="flex justify-center items-center w-full gap-2">
                  <button className="mt-2 px-3 py-1.5 bg-gray-700 rounded-lg hover:bg-gray-600 text-xs sm:text-sm">
                    Browse Device
                  </button>
                </div>
              </>
            ) : (
              preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                  onClick={() => setIsPreviewOpen(true)}
                />
              )
            )}
          </div>
        </div>

        {file && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleDelete}
              className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-500 text-sm sm:text-base"
            >
              Delete Image
            </button>
          </div>
        )}
      </div>

      <div className="w-full mt-6">
        <button
          className="w-full py-2 text-sm sm:text-base md:text-lg rounded-lg transition duration-300"
          onClick={handleUpload}
          disabled={!file}
          style={{
            backgroundColor: file ? "#1E40AF" : "#374151",
            cursor: file ? "pointer" : "not-allowed",
          }}
        >
          Upload
        </button>
      </div>

      {isPreviewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
          <button
            onClick={() => setIsPreviewOpen(false)}
            className="absolute top-4 right-4 text-white bg-red-700 p-2 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
          <div className="relative">
            <img
              src={preview}
              alt="Full Preview"
              className="max-w-9xl w-full overflow-hidden p-2 max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}

      {isCameraOpen && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-90 z-50 p-4">
          <button
            onClick={stopCamera}
            className="absolute top-4 right-4 text-white bg-red-700 p-2 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
          
          <div className="relative w-full max-w-lg">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline
              className="w-full h-auto rounded-lg"
            />
            <canvas 
              ref={photoRef} 
              className="hidden"
            />
          </div>
          
          <div className="mt-4 flex justify-center gap-4">
            <button
              onClick={toggleCamera}
              className="p-3 bg-gray-700 rounded-full hover:bg-gray-600"
              title="Switch camera"
            >
              <FlipHorizontal className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={takePhoto}
              className="p-4 bg-white rounded-full hover:bg-gray-200"
              title="Take photo"
            >
              <Camera className="w-8 h-8 text-black" />
            </button>
          </div>
          
          <p className="text-white mt-4 text-center">
            Position the lesion in the center and tap the camera button to capture
          </p>
        </div>
      )}
    </div>
  );
};

export default UploadPhoto;